/**
 * RequestMonitor - Tracks active axios requests and detects idle periods
 *
 * Monitors axios request/response lifecycle to determine when the application
 * is idle (no pending API requests). Used by prefetch services to schedule
 * background work during periods of low network activity.
 */

class RequestMonitor {
    constructor(idleTimeout = 500) {
        this.activeRequests = new Set();
        this.idleCallbacks = [];
        this.idleTimeout = idleTimeout;
        this.idleTimer = null;
        this.isAttached = false;
    }

    /**
     * Attach request/response interceptors to an axios instance
     * @param {Object} axiosInstance - The axios instance to monitor
     */
    attachToAxios(axiosInstance) {
        if (this.isAttached) {
            console.warn('RequestMonitor is already attached to an axios instance');
            return;
        }

        // Request interceptor - track outgoing requests
        axiosInstance.interceptors.request.use(
            (config) => {
                const requestId = this.generateRequestId();
                config.requestId = requestId;
                this.activeRequests.add(requestId);
                this.cancelIdleTimer();
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor - track completed requests
        axiosInstance.interceptors.response.use(
            (response) => {
                this.removeRequest(response.config.requestId);
                return response;
            },
            (error) => {
                if (error.config?.requestId) {
                    this.removeRequest(error.config.requestId);
                }
                return Promise.reject(error);
            }
        );

        this.isAttached = true;
    }

    /**
     * Generate a unique request ID
     * @returns {string} Unique request identifier
     */
    generateRequestId() {
        return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Remove a request from tracking and check if idle
     * @param {string} requestId - The request ID to remove
     */
    removeRequest(requestId) {
        this.activeRequests.delete(requestId);

        if (this.activeRequests.size === 0) {
            this.scheduleIdleCallback();
        }
    }

    /**
     * Schedule idle callback after timeout
     */
    scheduleIdleCallback() {
        this.cancelIdleTimer();

        this.idleTimer = setTimeout(() => {
            if (this.activeRequests.size === 0) {
                this.triggerIdleCallbacks();
            }
        }, this.idleTimeout);
    }

    /**
     * Cancel pending idle timer
     */
    cancelIdleTimer() {
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }

    /**
     * Trigger all registered idle callbacks
     */
    triggerIdleCallbacks() {
        this.idleCallbacks.forEach(callback => {
            try {
                callback();
            } catch (error) {
                console.error('Error in idle callback:', error);
            }
        });
    }

    /**
     * Register a callback to be called when system becomes idle
     * @param {Function} callback - Function to call when idle
     */
    onIdle(callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }
        this.idleCallbacks.push(callback);
    }

    /**
     * Check if system is currently idle
     * @returns {boolean} True if no active requests
     */
    isIdle() {
        return this.activeRequests.size === 0;
    }

    /**
     * Get count of active requests
     * @returns {number} Number of active requests
     */
    getActiveCount() {
        return this.activeRequests.size;
    }

    /**
     * Clear all callbacks and reset state
     */
    clear() {
        this.cancelIdleTimer();
        this.idleCallbacks = [];
        this.activeRequests.clear();
    }
}

export default RequestMonitor;
